(() => {
  const collectionRoot =
    document.querySelector('.collection') ||
    document.querySelector('#FacetFiltersForm') ||
    document.querySelector('[data-section-type="main-collection-product-grid"]') ||
    document.querySelector('[data-section-type="main-search"]');

  if (!collectionRoot) return;

  const whenIdle = (fn, timeout = 2000) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout });
    } else {
      setTimeout(fn, 300);
    }
  };

  const log = (msg) => {
    if (window.devLog) {
      window.devLog('PLPFeatures', msg);
    }
  };

  /**
   * Quick Add Module
   * Icon-based quick add with loading + success states
   */
  const QuickAdd = {
    selectors: {
      quickAddBtn: '[data-sa-quick-add]',
    },

    init() {
      const quickAddEnabled = window.SA_THEME?.quickAddEnabled === true;

      if (!quickAddEnabled) {
        log('Quick Add disabled via toggle');
        return;
      }

      const quickAddBtns = document.querySelectorAll(this.selectors.quickAddBtn);

      if (quickAddBtns.length === 0) {
        log('No quick add buttons found in DOM');
        return;
      }

      if (window.initOnce && !window.initOnce(collectionRoot, 'quick-add')) {
        log('Quick Add already initialized');
        return;
      }

      log(`Initializing Quick Add (${quickAddBtns.length} buttons)`);
      this.bindEvents(quickAddBtns);
    },

    bindEvents(buttons) {
      buttons.forEach((btn) => {
        btn.addEventListener('click', this.handleQuickAdd.bind(this));
      });
    },

    handleQuickAdd(event) {
      event.preventDefault();
      event.stopPropagation();
      const btn = event.currentTarget;
      const variantId = btn.dataset.variantId;

      if (variantId) {
        log(`Quick add variant: ${variantId}`);
        this.addToCart(variantId, btn);
      }
    },

    async addToCart(variantId, btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.classList.remove('is-success');

      try {
        const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = {
          id: variantId,
          quantity: 1,
        };

        if (cartUI && typeof cartUI.getSectionsToRender === 'function') {
          payload.sections = cartUI.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }

        log(`Added to cart: ${data.title}`);

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'quick-add',
            productVariantId: variantId,
            cartData: data,
          });
        }

        if (cartUI && cartUI.classList.contains('is-empty')) {
          cartUI.classList.remove('is-empty');
        }

        if (cartUI && typeof cartUI.renderContents === 'function' && data.sections) {
          cartUI.renderContents(data);
        }

        // Show success state
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');

        // Reset after delay
        setTimeout(() => {
          btn.classList.remove('is-success');
          btn.disabled = false;
        }, 1500);

        document.dispatchEvent(new CustomEvent('cart:add:success', {
          detail: { item: data },
        }));

      } catch (error) {
        log(`Quick add error: ${error.message}`);
        btn.classList.remove('is-loading');
        btn.disabled = false;

        document.dispatchEvent(new CustomEvent('cart:add:error', {
          detail: { error: error.message },
        }));
      }
    },
  };

  /**
   * Color Swatches Module
   * Swaps the product card image when a swatch is clicked
   */
  const ColorSwatches = {
    originalImages: new WeakMap(),

    selectors: {
      swatchContainer: '.p-product-card__swatches',
      swatch: '.p-product-card__swatch',
      primaryImage: '.p-product-card__img--primary',
      mediaLink: '.p-product-card__media-link',
      titleLink: '.p-product-card__title-link',
      quickAddLink: '.p-quick-add-icon[href]',
      contactTrigger: '[data-contact-request-cta]',
    },

    init() {
      const containers = document.querySelectorAll(this.selectors.swatchContainer);

      if (containers.length === 0) {
        log('No color swatches found');
        return;
      }

      if (window.initOnce && !window.initOnce(document.documentElement, 'color-swatches')) {
        log('Color Swatches already initialized');
        return;
      }

      log(`Initializing Color Swatches (${containers.length} cards)`);
      this.bindEvents(document);
    },

    bindEvents(root) {
      root.addEventListener('click', (event) => {
        const swatch = event.target.closest(this.selectors.swatch);
        if (!swatch || !root.contains(swatch)) return;

        event.preventDefault();
        event.stopPropagation();

        const container = swatch.closest(this.selectors.swatchContainer);
        if (!container) return;
        this.handleSwatchClick(swatch, container);
      });

      root.addEventListener('keydown', (event) => {
        const swatch = event.target.closest(this.selectors.swatch);
        if (!swatch || !root.contains(swatch)) return;

        const container = swatch.closest(this.selectors.swatchContainer);
        if (!container) return;
        this.handleSwatchKeydown(event, swatch, container);
      });

      root.addEventListener('mouseover', (event) => {
        const swatch = event.target.closest(this.selectors.swatch);
        if (!swatch || !root.contains(swatch) || swatch.contains(event.relatedTarget)) return;

        const container = swatch.closest(this.selectors.swatchContainer);
        if (!container) return;
        this.handleSwatchPreview(swatch, container);
      });

      root.addEventListener('mouseout', (event) => {
        const swatch = event.target.closest(this.selectors.swatch);
        if (!swatch || !root.contains(swatch) || swatch.contains(event.relatedTarget)) return;

        const container = swatch.closest(this.selectors.swatchContainer);
        if (!container) return;
        this.restoreSelectedImage(container);
      });
    },

    handleSwatchClick(swatch, container) {
      this.updateActiveState(swatch, container);

      const card = container.closest('.p-product-card');
      if (!card) return;

      const imageData = this.getSwatchImageData(swatch);

      const primaryImg = card.querySelector(this.selectors.primaryImage);
      if (primaryImg) {
        this.rememberOriginalImage(primaryImg);
        this.swapPrimaryImage(primaryImg, imageData.src ? imageData : this.getOriginalImageData(primaryImg));
      }

      this.updateCardLinks(card, swatch);
      this.updateContactTrigger(card, swatch);

      log(`Swatch: ${swatch.dataset.swatchValue} → selected`);
    },

    handleSwatchPreview(swatch, container) {
      const card = container.closest('.p-product-card');
      const primaryImg = card?.querySelector(this.selectors.primaryImage);
      if (!primaryImg) return;

      const imageData = this.getSwatchImageData(swatch);
      if (!imageData.src) return;

      this.rememberOriginalImage(primaryImg);
      container.classList.add('is-previewing');
      swatch.classList.add('is-previewing');
      this.swapPrimaryImage(primaryImg, imageData);
    },

    restoreSelectedImage(container) {
      container.querySelectorAll(`${this.selectors.swatch}.is-previewing`).forEach((swatch) => {
        swatch.classList.remove('is-previewing');
      });
      container.classList.remove('is-previewing');

      const card = container.closest('.p-product-card');
      const primaryImg = card?.querySelector(this.selectors.primaryImage);
      if (!primaryImg) return;

      const activeSwatch = container.querySelector(`${this.selectors.swatch}[aria-checked='true']`);
      const selectedImageData = activeSwatch ? this.getSwatchImageData(activeSwatch) : null;
      this.swapPrimaryImage(
        primaryImg,
        selectedImageData?.src ? selectedImageData : this.getOriginalImageData(primaryImg)
      );
    },

    handleSwatchKeydown(event, swatch, container) {
      const key = event.key;

      if (key === ' ' || key === 'Enter') {
        event.preventDefault();
        this.handleSwatchClick(swatch, container);
        return;
      }

      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
        return;
      }

      event.preventDefault();

      const swatches = Array.from(container.querySelectorAll(this.selectors.swatch));
      const currentIndex = swatches.indexOf(swatch);

      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % swatches.length;
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + swatches.length) % swatches.length;
      } else if (key === 'Home') {
        nextIndex = 0;
      } else if (key === 'End') {
        nextIndex = swatches.length - 1;
      }

      const nextSwatch = swatches[nextIndex];
      if (!nextSwatch) return;

      nextSwatch.focus();
      this.handleSwatchClick(nextSwatch, container);
    },

    updateActiveState(activeSwatch, container) {
      container.querySelectorAll(this.selectors.swatch).forEach((swatch) => {
        const isActive = swatch === activeSwatch;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-checked', isActive ? 'true' : 'false');
        swatch.tabIndex = isActive ? 0 : -1;
      });
    },

    getSwatchImageData(swatch) {
      return {
        src: swatch.dataset.swatchImage || '',
        srcset: swatch.dataset.swatchImageSrcset || '',
        sizes: swatch.dataset.swatchImageSizes || '',
        width: swatch.dataset.swatchImageWidth || '',
        height: swatch.dataset.swatchImageHeight || '',
        alt: swatch.dataset.swatchImageAlt || '',
      };
    },

    rememberOriginalImage(primaryImg) {
      if (this.originalImages.has(primaryImg)) return;

      this.originalImages.set(primaryImg, {
        src: primaryImg.getAttribute('src') || primaryImg.currentSrc || '',
        srcset: primaryImg.getAttribute('srcset') || '',
        sizes: primaryImg.getAttribute('sizes') || '',
        width: primaryImg.getAttribute('width') || '',
        height: primaryImg.getAttribute('height') || '',
        alt: primaryImg.getAttribute('alt') || '',
      });
    },

    getOriginalImageData(primaryImg) {
      this.rememberOriginalImage(primaryImg);
      return this.originalImages.get(primaryImg) || {};
    },

    swapPrimaryImage(primaryImg, imageData) {
      if (!imageData.src) return;

      if (primaryImg.swatchSwapTimer) {
        window.clearTimeout(primaryImg.swatchSwapTimer);
        primaryImg.swatchSwapTimer = null;
      }

      const currentSrc = new URL(primaryImg.getAttribute('src') || '', window.location.href).href;
      const nextSrc = new URL(imageData.src, window.location.href).href;
      if (currentSrc === nextSrc) {
        primaryImg.style.opacity = '1';
        return;
      }

      primaryImg.style.opacity = '0';
      primaryImg.swatchSwapTimer = window.setTimeout(() => {
        primaryImg.setAttribute('src', imageData.src);

        if (imageData.srcset) {
          primaryImg.setAttribute('srcset', imageData.srcset);
        } else {
          primaryImg.removeAttribute('srcset');
        }

        if (imageData.sizes) {
          primaryImg.setAttribute('sizes', imageData.sizes);
        } else {
          primaryImg.removeAttribute('sizes');
        }

        if (imageData.width) {
          primaryImg.setAttribute('width', imageData.width);
        }

        if (imageData.height) {
          primaryImg.setAttribute('height', imageData.height);
        }

        if (imageData.alt) {
          primaryImg.setAttribute('alt', imageData.alt);
        }

        primaryImg.style.opacity = '1';
      }, 140);
    },

    updateCardLinks(card, swatch) {
      const productUrl = swatch.dataset.swatchProductUrl;
      if (!productUrl) return;

      [this.selectors.mediaLink, this.selectors.titleLink, this.selectors.quickAddLink].forEach((selector) => {
        const link = card.querySelector(selector);
        if (link) {
          link.setAttribute('href', productUrl);
        }
      });
    },

    updateContactTrigger(card, swatch) {
      const contactTrigger = card.querySelector(this.selectors.contactTrigger);
      if (!contactTrigger) return;

      const swatchHref = swatch.getAttribute('data-contact-request-href');
      if (swatchHref) {
        contactTrigger.setAttribute('href', swatchHref);
      }

      [
        'data-contact-request-variant-id',
        'data-contact-request-variant-title',
        'data-contact-request-sku',
        'data-contact-request-product-url',
        'data-contact-request-product-image',
      ].forEach((attributeName) => {
        const attributeValue = swatch.getAttribute(attributeName);
        if (attributeValue) {
          contactTrigger.setAttribute(attributeName, attributeValue);
        } else {
          contactTrigger.removeAttribute(attributeName);
        }
      });
    },
  };

  function init() {
    document.documentElement.classList.add('sa-plp-ready');
    log('PLP init starting');

    QuickAdd.init();
    ColorSwatches.init();

    log('PLP init complete');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => whenIdle(init));
  } else {
    whenIdle(init);
  }
})();
